import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { FIELD_DEFS } from "@/lib/scParse";

const NONE = "__none__";

export function MappingModal({ open, onOpenChange, doctype, columns, initialMapping, onConfirm }) {
  const fields = FIELD_DEFS[doctype] || [];
  const [map, setMap] = useState({});

  useEffect(() => {
    if (open) setMap(initialMapping || {});
  }, [open, initialMapping]);

  const missingRequired = fields.filter((f) => f.required && !map[f.key]).map((f) => f.label);
  const needsLane = ["allocation", "booked", "forward", "ratecard"].includes(doctype);
  const laneMissing = needsLane && !map.lane && !(map.origin && map.destination);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mapping-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Map your columns</DialogTitle>
          <DialogDescription>
            Match each field to a column from your file. Your choices are remembered for next time.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[52vh] overflow-y-auto pr-1 space-y-3">
          {fields.map((f) => (
            <div key={f.key} className="flex items-center gap-3">
              <label className="w-40 shrink-0 text-sm text-slate-600">
                {f.label}{f.required && <span className="text-accent"> *</span>}
              </label>
              <Select
                value={map[f.key] || NONE}
                onValueChange={(v) => setMap((m) => ({ ...m, [f.key]: v === NONE ? undefined : v }))}
              >
                <SelectTrigger className="flex-1" data-testid={`map-select-${f.key}`}>
                  <SelectValue placeholder="— not mapped —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— not mapped —</SelectItem>
                  {columns.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        {(missingRequired.length > 0 || laneMissing) && (
          <p className="text-xs text-accent">
            {missingRequired.length > 0 && `Required: ${missingRequired.join(", ")}. `}
            {laneMissing && "Map a Lane, or both Origin and Destination."}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="mapping-cancel">Cancel</Button>
          <Button
            data-testid="mapping-confirm"
            disabled={missingRequired.length > 0 || laneMissing}
            onClick={() => onConfirm(Object.fromEntries(Object.entries(map).filter(([, v]) => v)))}
          >
            Save &amp; import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
